import { FC, useEffect, useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DatePickerField } from "../../FormFields";
import {
  IPlace,
  ISchedule,
  IService,
  IUser,
  TypeOfDays,
  checkIfChosenServicePossibleAtTime,
  getBookedVisitTime,
  getTime,
  getTimeNumbersInArray,
  getUnscheduleDates,
  isRequired,
  placeQueryData,
  servicePaginationData,
  userRoles,
} from "../../../shared";
import styles from "./visitForm.module.scss";
import {
  placeApi,
  scheduleApi,
  serviceApi,
  userApi,
  visitApi,
} from "../../../store/services";
import {
  useVisitListByServiceAndWorkerMap,
  useProcessStageAction,
} from "../../../hooks";
import { IVisitForm } from "../VisitForm";
import { Modal } from "../../Modal";
import { ModalContentContainer } from "../../ModalContentContainer";
import { Button } from "../../Buttons";

export const CalendarVisitFormComponent: FC<IVisitForm> = ({
  onSubmit,
  initialValues,
  visitId,
  isVisitFromCalendar,
  selectedPatient,
}) => {
  const { t } = useTranslation();

  const [unscheduleDates, setUnscheduleDates] = useState<Date[]>();
  const [openAt, setOpenAt] = useState<number[]>();
  const [closeAt, setCloseAt] = useState<number[]>();
  const [excludeTimes, setExcludeTimes] = useState<Date[]>();

  const [chosenClinic, setChosenClinic] = useState<IPlace>();
  const [chosenDate, setChosenDate] = useState<Date | undefined>();
  const [chosenDoctor, setChosenDoctor] = useState<IUser>();
  const [chosenService, setChosenService] = useState<IService>();

  const [filteredServices, setFilteredServices] = useState<IService[]>();

  const { data: fechedWorkerList } = userApi.useFetchWorkerListQuery(
    {
      page: 1,
      limit: 100,
      roles: [userRoles.doctor, userRoles.rehabilitator],
      serviceId: chosenService?._id,
      placeId: chosenClinic?._id,
    },
    {
      skip: !chosenClinic,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: fetchedHospitalList } =
    placeApi.useFetchPlaceListQuery(placeQueryData);

  const { data: fetchedServiceList } = serviceApi.useFetchServiceListQuery(
    servicePaginationData,
  );

  const { data: fetchedVisitList } = visitApi.useFetchPublicVisitListQuery(
    {
      from: moment(chosenDate).startOf("day").toISOString(true),
      to: moment(chosenDate).endOf("day").toISOString(true),
      doctorId: chosenDoctor?._id,
      placeId: chosenClinic?._id,
    },
    {
      skip: !chosenDoctor || !chosenDate,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: fetchedSchedule } = scheduleApi.useFetchUserScheduleQuery(
    {
      userId: chosenDoctor?._id,
      placeId: chosenClinic?._id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !chosenDoctor,
    },
  );

  const { data: fetchedPatient } = userApi.useFetchPatientQuery(
    selectedPatient?._id as string,
    {
      skip: !selectedPatient?._id,
    },
  );

  const serviceList = fetchedServiceList?.items;
  const workerList = fechedWorkerList?.items;
  const hospitalList = fetchedHospitalList?.items;
  const visitList = fetchedVisitList?.items;

  const {
    isOpen,
    openConfirmReturnToStart,
    setOpenConfirmReturnToStart,
    isLoading,
    onHandleContinueExercises,
    onHandleConfirm,
  } = useProcessStageAction({
    isPatient: false,
    patientId: fetchedPatient?._id || "",
    pendingToChooseStageAction:
      fetchedPatient?.pendingToChooseStageAction || false,
  });

  const handleFilteredDate = (date: Date) => {
    const day = date?.getDay();
    return !chosenClinic?.schedule[day as TypeOfDays].isHoliday;
  };

  const workerListByScheduleAndService = useMemo(() => {
    if (!fetchedSchedule?.length) {
      return [];
    }
    const isRehabilitatorRequired = chosenService?.isRelatedToExercise;

    return fetchedSchedule?.filter((schedule) => {
      const hasService = schedule.user?.portfolio?.serviceIds?.includes(
        chosenService?._id as string,
      );

      const isRoleValid = isRehabilitatorRequired
        ? schedule.user?.role === userRoles.rehabilitator
        : true;

      return hasService && isRoleValid;
    });
  }, [fetchedSchedule, chosenService]);

  const visitListByServiceAndWorkerMap = useVisitListByServiceAndWorkerMap({
    visits: visitList,
    workerListByScheduleAndService,
    chosenService,
  });

  const isAvailableTime = useMemo(() => {
    return checkIfChosenServicePossibleAtTime(
      chosenDate as Date,
      chosenService,
      visitListByServiceAndWorkerMap.generalBookedTime,
    );
  }, [
    chosenDate,
    chosenService,
    visitListByServiceAndWorkerMap.generalBookedTime,
  ]);

  const scheduleByChosenDate = fetchedSchedule?.find(
    (schedule) =>
      moment(schedule?.startAt).format("DD.MM.YYYY") ===
      moment(chosenDate).format("DD.MM.YYYY"),
  );

  useEffect(() => {
    if (isVisitFromCalendar && chosenDoctor) {
      const activeServices = chosenDoctor?.portfolio?.services.filter(
        (service) => service.isActive,
      );
      setFilteredServices(activeServices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenDoctor]);

  useEffect(() => {
    if (fetchedSchedule) {
      const unscheduleDatesArray = getUnscheduleDates(
        new Date(),
        moment().add(2, "month").toDate(),
        fetchedSchedule as ISchedule[],
      );
      setUnscheduleDates(unscheduleDatesArray);
    }
  }, [fetchedSchedule]);

  useEffect(() => {
    if (chosenDate && chosenService) {
      setExcludeTimes(getBookedVisitTime(visitList, chosenService as IService));
    }
  }, [chosenDate, chosenDoctor, chosenService, visitList]);

  useEffect(() => {
    if (scheduleByChosenDate) {
      setOpenAt(() => getTimeNumbersInArray(scheduleByChosenDate?.startAt));
      setCloseAt(() => getTimeNumbersInArray(scheduleByChosenDate?.finishAt));
    }
  }, [chosenDate, chosenClinic, scheduleByChosenDate]);

  useEffect(() => {
    if (initialValues?.doctorId) {
      setChosenDoctor(
        workerList?.find((worker) => worker._id === initialValues?.doctorId),
      );
      setChosenClinic(
        hospitalList?.find((clinic) => clinic._id === initialValues?.placeId),
      );
      setChosenDate(new Date(initialValues?.startTime as string));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={(visitId || isVisitFromCalendar) && initialValues}
      render={({
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
        submitFailed,
        submitErrors,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className={`${styles.select_wrap} select_wrap`}>
            <label htmlFor="serviceId">
              {t("adminPanel.form.serviceLabel")}*
            </label>
            <Field
              id="serviceId"
              name="serviceId"
              component="select"
              validate={isRequired}
              onChange={(e: any) => {
                form.change("serviceId", e.target.value);
                const service = serviceList?.find(
                  (service) => service._id === e.target.value,
                );
                setChosenService(service as IService);
              }}
            >
              <option value="" disabled>
                {t("adminPanel.form.chooseService")}
              </option>
              {(filteredServices || serviceList)?.map((service) => (
                <option value={service._id} key={service._id}>
                  {service.title}
                </option>
              ))}
            </Field>
            <Field
              name="serviceId"
              subscription={{ error: true }}
              render={({ meta }) =>
                submitFailed &&
                meta.error && <span className="error">{t(meta.error)}</span>
              }
              className="error"
            />
            {isAvailableTime && (
              <span className="error">
                {t("adminPanel.form.noAvailableTime")}
              </span>
            )}
          </div>

          <div className={`${styles.select_wrap} select_wrap`}>
            <label>{t("adminPanel.form.clinicLabel")}*</label>
            <Field
              name="placeId"
              component="select"
              validate={isRequired}
              disabled
              onChange={(e: any) => {
                form.change("placeId", e.target.value);
                const clinic = hospitalList?.find(
                  (clinic) => clinic._id === e.target.value,
                );
                setChosenClinic(clinic);
                setChosenDate(undefined);
              }}
            >
              <option value="" disabled>
                {t("adminPanel.form.chooseClinic")}
              </option>
              {hospitalList?.map((clinic) => (
                <option value={clinic._id} key={clinic._id}>
                  {clinic.title}
                </option>
              ))}
            </Field>
            <Field
              name="placeId"
              subscription={{ error: true }}
              render={({ meta }) =>
                submitFailed &&
                meta.error && <span className="error">{t(meta.error)}</span>
              }
              className="error"
            />
          </div>

          <div className={`${styles.select_wrap} select_wrap`}>
            <label>{t("adminPanel.form.chooseWorkerLabel")}*</label>
            <Field
              name="doctorId"
              component="select"
              disabled
              validate={isRequired}
              onChange={(e: any) => {
                form.change("doctorId", e.target.value);
                setChosenDoctor(
                  workerList?.find((spec) => spec._id === e.target.value),
                );
              }}
            >
              <option value="" disabled>
                {t("adminPanel.form.chooseWorker")}
              </option>
              {workerList?.map((doctor) => (
                <option value={doctor._id} key={doctor._id}>
                  {doctor.firstName} {doctor.lastName}
                </option>
              ))}
            </Field>
            <Field
              name="doctorId"
              subscription={{ error: true }}
              render={({ meta }) =>
                submitFailed &&
                meta.error && <span className="error">{t(meta.error)}</span>
              }
              className="error"
            />
          </div>

          <div className={styles.date_wrap}>
            <label>{t("adminPanel.form.dateLabel")}*</label>
            <Field
              name="startTime"
              validate={isRequired}
              component={DatePickerField}
              handleFilteredDate={handleFilteredDate}
              setChosenDate={setChosenDate}
              chosenDate={chosenDate}
              excludeTimes={excludeTimes}
              excludeDates={unscheduleDates}
              isDisabled
              minTime={getTime(openAt?.[0], openAt?.[1])}
              maxTime={getTime(closeAt?.[0] && closeAt?.[0] - 1, closeAt?.[1])}
            />
          </div>
          {isOpen && (
            <div className={styles.long_term_inactive_warning}>
              <p className={styles.long_term_inactive_warning_text}>
                {t("longTermInactive.patientWasInactive")}
              </p>
              <div className={styles.long_term_inactive_warning_buttons}>
                <Button
                  label={t("buttons.continueExercises")}
                  className="green_outlined"
                  customStyle={styles.longTermContinueButton}
                  onClick={onHandleContinueExercises}
                  disabled={isLoading}
                />
                <Button
                  label={t("buttons.returnToStart")}
                  className="small_green"
                  customStyle={styles.longTermContinueButton}
                  onClick={() => setOpenConfirmReturnToStart(true)}
                  disabled={isLoading}
                />
              </div>
              <Modal
                isOpen={openConfirmReturnToStart}
                customStyles={styles.longTermInactiveModal}
                blockClose
              >
                <ModalContentContainer
                  modalTitle={t("longTermInactive.approvalTitle")}
                  subtitle={t("longTermInactive.staffApprovalSubtitle")}
                  customStyles={styles.longTermInactiveModalContent}
                >
                  <Button
                    label={t("confirmModal.confirm")}
                    className="small_green"
                    customStyle={styles.longTermContinueButton}
                    onClick={onHandleConfirm}
                    disabled={isLoading}
                  />
                  <Button
                    label={t("confirmModal.cancel")}
                    className="orange_outlined"
                    customStyle={styles.longTermContinueButton}
                    onClick={() => setOpenConfirmReturnToStart(false)}
                    disabled={isLoading}
                  />
                </ModalContentContainer>
              </Modal>
            </div>
          )}

          <button
            type="submit"
            className={styles.form_button}
            disabled={submitting || fetchedPatient?.pendingToChooseStageAction}
          >
            {t("buttons.create")}
          </button>
        </form>
      )}
    />
  );
};
